import type { NoteEvent, Score, VoiceItem } from '../score/types';
import { describeEvent } from '../score/voiceEdit';

export interface AdjacentScoreEvents {
  previousId: string | null;
  nextId: string | null;
}

function* eventsInWrittenOrder(items: VoiceItem[]): Generator<NoteEvent> {
  for (const item of items) {
    if (item.kind === 'tuplet') yield* eventsInWrittenOrder(item.items);
    else yield item;
  }
}

/** Match the selectable events in scoreToStaffSong, including its grace-run
 * visibility rules. A chord has one selectable event ID: its top notehead is
 * the editor's pitch target, and its other heads travel with that event. */
function visibleEventIds(items: VoiceItem[], staffIndex: number): string[] {
  const ids: string[] = [];
  let graces: string[] = [];
  for (const event of eventsInWrittenOrder(items)) {
    if ((event.staff ?? staffIndex) !== staffIndex) continue;
    if (event.grace) {
      if (event.kind === 'note' && event.notes.length > 0) graces.push(event.id);
      continue;
    }
    if (event.kind === 'note' && event.notes.length > 0) ids.push(...graces);
    graces = [];
    if (event.kind === 'rest' || event.slash || event.notes.length > 0) ids.push(event.id);
  }
  // A grace run without a following pitched host does not engrave.
  return ids;
}

/** Previous/next written event in the selected part, home staff and local
 * voice. Empty bars are skipped; navigation never follows repeats, enters a
 * different voice, changes score content or invents a new bar. Model IDs are
 * also the rendered IDs, so page windows need no special mapping. */
export function adjacentScoreEvents(score: Score, selectedId: string | null): AdjacentScoreEvents {
  const empty = { previousId: null, nextId: null };
  if (!selectedId) return empty;
  const selected = describeEvent(score, selectedId);
  if (!selected || selected.measureIndex >= score.form.length) return empty;

  const part = score.parts[selected.partIndex];
  const ids = score.form.flatMap((_, measureIndex) => {
    const voice = part.measures[measureIndex]?.voices.filter(
      (candidate) => (candidate.staff ?? 0) === selected.staffIndex,
    )[selected.voiceIndex];
    return voice ? visibleEventIds(voice.items, selected.staffIndex) : [];
  });
  const index = ids.indexOf(selectedId);
  if (index < 0) return empty;
  return {
    previousId: ids[index - 1] ?? null,
    nextId: ids[index + 1] ?? null,
  };
}
